/**
 * Buchhaltungs-Export (Erlöse + Zahlungen + Stornos) für Zeitraum oder Monat.
 */

import { API_BASE, ApiError } from "@/lib/apiClient";

export type AccountingExportFormat = "csv" | "datev";

type DateRangeExportParams = {
  fromDate: string;
  toDate: string;
  format?: AccountingExportFormat;
};

type MonthlyExportParams = {
  year: number;
  month: number;
  format?: AccountingExportFormat;
};

function buildRangeExportUrl({ fromDate, toDate, format = "csv" }: DateRangeExportParams): string {
  const params = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    format,
  });
  const path = `/api/v1/exports/accounting?${params}`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

function buildMonthlyExportUrl({ year, month, format = "datev" }: MonthlyExportParams): string {
  const params = new URLSearchParams({
    year: String(year),
    month: String(month),
    format,
  });
  const path = `/api/v1/exports/accounting/monthly?${params}`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
}

async function downloadExport(url: string, fallbackFilename: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "include" });
  } catch (err) {
    const message = err instanceof Error ? err.message : "Export konnte nicht geladen werden.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const parsed = (await res.json()) as { detail?: unknown };
        if (typeof parsed.detail === "string") message = parsed.detail;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get("Content-Disposition"), fallbackFilename);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export async function downloadAccountingExport(
  fromDate: string,
  toDate: string,
  format: AccountingExportFormat = "csv",
): Promise<void> {
  await downloadExport(
    buildRangeExportUrl({ fromDate, toDate, format }),
    format === "datev"
      ? `EXTF_Buchungsstapel_${fromDate}_${toDate}.csv`
      : `buchhaltung_${fromDate}_${toDate}.csv`,
  );
}

export async function downloadMonthlyAccountingExport(
  year: number,
  month: number,
  format: AccountingExportFormat = "datev",
): Promise<void> {
  const monthLabel = String(month).padStart(2, "0");
  await downloadExport(
    buildMonthlyExportUrl({ year, month, format }),
    format === "datev"
      ? `EXTF_Buchungsstapel_${year}-${monthLabel}.csv`
      : `buchhaltung_${year}-${monthLabel}.csv`,
  );
}
